<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick, watch } from 'vue';
import { cloneDeep } from 'lodash';
import MessageBubble from './MessageBubble.vue';
import MessageComposer from './MessageComposer.vue';
import useChatStore from '@/stores/communication/chat';
import useAuthStore from '@/stores/authentication/auth';
import { MeRef } from '@/plugins/interfaces/Me';
import Message from '@/stores/communication/Message';

const props = defineProps<{ uuid: string; displayName: string | null }>();

const chatStore = useChatStore();

const messagesList = ref<Message[]>([]);

const userAuth = useAuthStore();

const me = ref(cloneDeep(MeRef));

const senderNameVisibility = ref<Map<string, boolean>>(new Map());

let pollInterval: number | null = null;

async function loadMessages() {
	chatStore.loading = true;
	try {
		const res = await chatStore.fetchMessages(props.uuid);
		messagesList.value = res.data.data || [];
		await nextTick();
		scrollToBottom();
	} finally {
		chatStore.loading = false;
	}
}

function getSenderNameVisibility(messageUuid: string): boolean {
	return senderNameVisibility.value.get(messageUuid) ?? false;
}

function scrollToBottom() {
	const el = document.querySelector('.chat-window') as HTMLElement;
	if (el) el.scrollTop = el.scrollHeight;
}

function onSent(msg: any) {
	// push to list and scroll
	messagesList.value.unshift(msg); // messages come in descending order; keep consistent
	nextTick(() => scrollToBottom());
}

watch(
	messagesList,
	(newMessages) => {
		senderNameVisibility.value.clear();
		let prevSenderId: number | null = null;
		newMessages.forEach((msg) => {
			const senderId = msg.sender?.id || null;
			if (prevSenderId !== senderId) {
				senderNameVisibility.value.set(msg.uuid, true);
				prevSenderId = senderId;
			} else {
				senderNameVisibility.value.set(msg.uuid, false);
			}
		});
	},
	{ deep: true }
);

onMounted(async () => {
	userAuth
		.authMe()
		.then((response) => {
			if (response.status === 200) me.value = userAuth.getMe;
		})
		.catch((error) => {
			console.log(error);
		});

	await loadMessages();
	// polling for new messages every 2.5s (adjust)
	pollInterval = window.setInterval(async () => {
		try {
			// fetch newest messages only
			const res = await chatStore.fetchMessages(props.uuid, { per_page: 20 });
			const newList = res.data.data || [];
			messagesList.value = newList;
		} catch (e) {
			// ignore polling errors
		}
	}, 2500);
});

onUnmounted(() => {
	if (pollInterval) clearInterval(pollInterval);
});
</script>

<style scoped>
.chat-window {
	background: #fafafa;
}
</style>

<template>
	<v-card flat>
		<v-card-title
			class="d-flex align-center"
			v-if="displayName != null && chatStore.loading === false"
		>
			<div class="d-flex align-center">
				<div class="text-h6">
					<v-icon color="teal">mdi-account-circle</v-icon>
					{{ displayName }}
				</div>
			</div>
		</v-card-title>

		<v-card-text
			class="chat-window"
			style="height: 60vh; overflow: auto"
			ref="scrollRef"
		>
			<div v-if="chatStore.loading" class="text-center pa-4">
				<v-progress-circular indeterminate color="primary" /> loading
				conversation...
			</div>

			<div v-else>
				<div v-for="m in messagesList" :key="m.uuid" class="mb-2">
					<MessageBubble
						:message="m"
						:meId="me.me.id"
						:showSenderName="getSenderNameVisibility(m.uuid)"
					/>
				</div>

				<div v-if="!messagesList.length" class="text-center text--secondary">
					No messages
				</div>
			</div>
		</v-card-text>

		<v-divider />

		<v-card-actions>
			<MessageComposer :conversationUuid="props.uuid" @sent="onSent" />
		</v-card-actions>
	</v-card>
</template>
